import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Form, Input, Typography, message } from 'antd';
import {
  RobotOutlined,
  SafetyCertificateOutlined,
  QuestionCircleOutlined,
  RightOutlined,
  ArrowRightOutlined,
  SnippetsOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { telegram } from '../../utils/telegram';
import OnboardingFrame, { OnboardingCard, CardSectionHeader } from './OnboardingFrame';

const { Text } = Typography;

interface Props {
  value: string;
  onChange: (value: string) => void;
  onNext: () => void;
  onBack: () => void;
  serverError?: string;
}

const botTokenPattern = /^\d{8,10}:[A-Za-z0-9_-]{35,}$/;

// Telegram brand blue — used for the BotFather call-to-action so it stays
// visually distinct from the native MainButton ("Davom etish").
const tgGradient = 'linear-gradient(135deg, #2AABEE 0%, #229ED9 100%)';

const miniActionBtnStyle: CSSProperties = {
  flexShrink: 0,
  border: '1px solid rgba(34,158,217,0.35)',
  background: 'rgba(34,158,217,0.10)',
  color: '#1c8ec9',
  borderRadius: 8,
  padding: '4px 10px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  WebkitTapHighlightColor: 'transparent',
};

export default function Step2BotToken({ value, onChange, onNext, serverError }: Props) {
  const { t } = useTranslation('owner');
  const [form] = Form.useForm();
  // Open by default so a first-time user immediately sees how to get a token.
  const [guideOpen, setGuideOpen] = useState(true);
  const [pasting, setPasting] = useState(false);

  // Stable refs for MainButton handler.
  const formRef = useRef(form);
  formRef.current = form;
  const onNextRef = useRef(onNext);
  onNextRef.current = onNext;

  useEffect(() => {
    if (serverError) {
      form.setFields([{ name: 'botToken', errors: [serverError] }]);
    }
  }, [serverError, form]);

  useEffect(() => {
    const handleNext = () => {
      formRef.current
        .validateFields()
        .then(() => onNextRef.current())
        .catch(() => {});
    };
    telegram.setMainButton(t('onboarding.mainButton.continue'), handleNext);
    return () => telegram.hideMainButton();
  }, [t]);

  // Keep parent state, the form store and the validation state in sync.
  const applyToken = (token: string) => {
    onChange(token);
    form.setFieldsValue({ botToken: token });
    form.setFields([{ name: 'botToken', errors: [] }]);
  };

  const handleOpenBotFather = () => {
    telegram.hapticFeedback.impact('light');
    telegram.openTelegramUser('BotFather');
  };

  const handlePaste = async () => {
    telegram.hapticFeedback.selection();
    setPasting(true);
    try {
      const text = await telegram.readClipboardText();
      if (!text) {
        void message.info(t('onboarding.step2.paste.empty'));
        return;
      }
      if (!botTokenPattern.test(text)) {
        telegram.hapticFeedback.notification('warning');
        void message.warning(t('onboarding.step2.paste.invalid'));
        return;
      }
      applyToken(text);
      telegram.hapticFeedback.notification('success');
      void message.success(t('onboarding.step2.paste.success'));
    } finally {
      setPasting(false);
    }
  };

  const handleCopyCommand = async () => {
    try {
      await navigator.clipboard.writeText('/newbot');
      telegram.hapticFeedback.notification('success');
      void message.success(t('onboarding.step2.guide.commandCopied'));
    } catch {
      telegram.hapticFeedback.notification('error');
      void message.error(t('onboarding.step2.guide.copyFailed'));
    }
  };

  const guideSteps: Array<{ text: string; action?: 'open' | 'copy' }> = [
    { text: t('onboarding.step2.guide.step1'), action: 'open' },
    { text: t('onboarding.step2.guide.step2'), action: 'copy' },
    { text: t('onboarding.step2.guide.step3') },
    { text: t('onboarding.step2.guide.step4') },
  ];

  return (
    <OnboardingFrame
      currentStep={2}
      badge={t('onboarding.step2.badge')}
      title={t('onboarding.step2.title')}
      subtitle={t('onboarding.step2.subtitle')}
    >
      {/* ─── Open BotFather CTA ─────────────────────────────────────── */}
      <button
        type="button"
        onClick={handleOpenBotFather}
        style={{
          width: '100%',
          border: 'none',
          borderRadius: 16,
          background: tgGradient,
          color: '#fff',
          padding: '14px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          cursor: 'pointer',
          marginBottom: 12,
          textAlign: 'left',
          boxShadow: '0 6px 16px rgba(34,158,217,0.28)',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 12,
            background: 'rgba(255,255,255,0.20)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 19,
            flexShrink: 0,
          }}
        >
          <RobotOutlined />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: '#fff',
              display: 'block',
            }}
          >
            {t('onboarding.step2.openBotFather.title')}
          </Text>
          <Text
            style={{
              fontSize: 12,
              color: 'rgba(255,255,255,0.85)',
              display: 'block',
              lineHeight: 1.35,
            }}
          >
            {t('onboarding.step2.openBotFather.subtitle')}
          </Text>
        </div>
        <ArrowRightOutlined style={{ fontSize: 16, color: '#fff', flexShrink: 0 }} />
      </button>

      {/* ─── Token input card ──────────────────────────────────────── */}
      <OnboardingCard>
        <CardSectionHeader
          icon={<RobotOutlined />}
          iconColor="#1677ff"
          title={t('onboarding.step2.fieldLabel')}
          subtitle={t('onboarding.step2.helper')}
        />

        <Form
          form={form}
          layout="vertical"
          initialValues={{ botToken: value }}
          requiredMark={false}
        >
          <Form.Item
            name="botToken"
            style={{ marginBottom: 0 }}
            rules={[
              { required: true, message: t('onboarding.step2.validation.required') },
              { min: 40, message: t('onboarding.step2.validation.min') },
              { max: 100, message: t('onboarding.step2.validation.max') },
              {
                pattern: botTokenPattern,
                message: t('onboarding.step2.validation.pattern'),
              },
            ]}
          >
            <Input.Password
              size="large"
              placeholder={t('onboarding.step2.placeholder')}
              value={value}
              onChange={(e) => {
                onChange(e.target.value);
              }}
              autoComplete="off"
              style={{
                borderRadius: 12,
                fontSize: 14,
                fontFamily: 'var(--bl-font-mono, ui-monospace)',
              }}
            />
          </Form.Item>
        </Form>

        {/* Paste token from clipboard (one-tap after copying from BotFather) */}
        <button
          type="button"
          onClick={handlePaste}
          disabled={pasting}
          style={{
            width: '100%',
            marginTop: 12,
            border: '1px dashed rgba(34,158,217,0.45)',
            background: 'rgba(34,158,217,0.06)',
            color: '#1c8ec9',
            borderRadius: 12,
            padding: '10px 12px',
            fontSize: 13,
            fontWeight: 600,
            cursor: pasting ? 'default' : 'pointer',
            opacity: pasting ? 0.6 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <SnippetsOutlined style={{ fontSize: 15 }} />
          {pasting
            ? t('onboarding.step2.paste.loading')
            : t('onboarding.step2.paste.button')}
        </button>
      </OnboardingCard>

      {/* ─── Guide (collapsible) ───────────────────────────────────── */}
      <OnboardingCard style={{ padding: 0, overflow: 'hidden' }}>
        <button
          type="button"
          onClick={() => {
            telegram.hapticFeedback.selection();
            setGuideOpen((prev) => !prev);
          }}
          style={{
            width: '100%',
            border: 'none',
            background: 'transparent',
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            cursor: 'pointer',
            textAlign: 'left',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'rgba(146,84,222,0.16)',
              color: '#9254de',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 17,
              flexShrink: 0,
            }}
          >
            <QuestionCircleOutlined />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: 'var(--bl-text-strong, #0f172a)',
                display: 'block',
              }}
            >
              {t('onboarding.step2.guide.title')}
            </Text>
          </div>
          <RightOutlined
            style={{
              fontSize: 12,
              color: 'var(--bl-text-muted, #9ca3af)',
              transform: guideOpen ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          />
        </button>

        {guideOpen && (
          <div
            className="bl-animate-in"
            style={{
              padding: '0 16px 16px 16px',
              borderTop: '1px solid var(--bl-divider, #f0f0f0)',
              paddingTop: 14,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            {guideSteps.map((step, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background:
                      idx === 0
                        ? 'linear-gradient(135deg, #9254de, #b37feb)'
                        : 'var(--bl-surface-soft, #f0f2f5)',
                    color: idx === 0 ? '#fff' : 'var(--bl-text-muted, #6b7280)',
                    fontSize: 11,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {idx + 1}
                </div>
                <Text
                  style={{
                    fontSize: 13,
                    color: 'var(--bl-text-strong, #0f172a)',
                    lineHeight: 1.5,
                    flex: 1,
                  }}
                >
                  {step.text}
                </Text>
                {step.action === 'open' && (
                  <button type="button" onClick={handleOpenBotFather} style={miniActionBtnStyle}>
                    {t('onboarding.step2.guide.openAction')}
                  </button>
                )}
                {step.action === 'copy' && (
                  <button type="button" onClick={handleCopyCommand} style={miniActionBtnStyle}>
                    <CopyOutlined style={{ fontSize: 12 }} />
                    {t('onboarding.step2.guide.copyAction')}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </OnboardingCard>

      {/* ─── Security warning ──────────────────────────────────────── */}
      <div
        style={{
          position: 'relative',
          borderRadius: 16,
          background:
            'linear-gradient(135deg, rgba(250,173,20,0.10) 0%, rgba(250,140,22,0.10) 100%)',
          border: '1px solid rgba(250,173,20,0.22)',
          padding: '14px 14px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          marginBottom: 12,
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: 'rgba(250,173,20,0.22)',
            color: '#d46b08',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 17,
            flexShrink: 0,
          }}
        >
          <SafetyCertificateOutlined />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--bl-text-strong, #0f172a)',
              display: 'block',
              marginBottom: 2,
            }}
          >
            {t('onboarding.step2.security.title')}
          </Text>
          <Text
            style={{
              fontSize: 12,
              color: 'var(--bl-text-muted, #6b7280)',
              display: 'block',
              lineHeight: 1.45,
            }}
          >
            {t('onboarding.step2.security.description')}
          </Text>
        </div>
      </div>
    </OnboardingFrame>
  );
}
